import React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CreditCardList from '../../../components/braintree/CreditCardList';
import { BillingAPI } from '../../../api';
import { STATUS_ACTIVE, STATUS_PENDING, STATUS_PAST_DUE, STATUS_CANCELED, STATUS_NONE} from '../../../api/billing';
import Button from '../../../components/Button';
import Confirmation from '../../../components/Confirmation';
import AddPaymentMethodDialogue from '../../../components/braintree/AddPaymentMethodDialogue';
import AccreditationSummary from '../../../components/AccreditationSummary';
import Alert from '@material-ui/lab/Alert';
import MatchingFeesTable from './MatchingFeesTable';
import { useHistory } from 'react-router-dom';

const paymentMethodAdvice = 'To update payment details, first add a new payment method, then remove the old one.';

const BillingSettings = ({providerUuid}) => {
  const history = useHistory();
  const [busy, setBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState();
  const [selectedCard, setSelectedCard] = useState();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [cardToDelete, setCardToDelete] = useState();
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [addErrorMessage, setAddErrorMessage] = useState();
  const [subscription, setSubscription] = useState();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [matchingFees, setMatchingFees] = useState();
  const cancellable = [STATUS_ACTIVE, STATUS_PENDING, STATUS_PAST_DUE];
  const allowCancel = subscription && subscription.accredited && cancellable.includes(subscription.status);
  const enrollable = [STATUS_CANCELED, STATUS_NONE];
  const allowEnroll = subscription && !subscription.accredited && enrollable.includes(subscription.status);
  const showPaymentMethodAdvice = subscription && cancellable.includes(subscription.status);
  const allowFreeTrial = subscription && subscription.status === STATUS_NONE;

  const getSubscriptionData = async () => {
    try {
      const { payload } = await BillingAPI.getSubscription(providerUuid);
      setSubscription(payload.subscription);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  };

  const onSave = async() => {
    setBusy(true);
    try {
      const { payload } = await BillingAPI.selectPaymentMethod(providerUuid, selectedCard.token);
      setPaymentMethods(payload.payment_methods);
      setSelectedCard(payload.payment_methods.find(card => card.selected));
    } finally {
      setBusy(false);
    }
  };

  const getAccredited = () => {
    history.push(`/provider/${providerUuid}/getaccredited`, {state: allowFreeTrial});
  };

  const deleteCard = async(deletedCard) => {
    setBusy(true);
    try {
      const { payload } = await BillingAPI.deletePaymentMethod(providerUuid, deletedCard.token);
      setPaymentMethods(payload.payment_methods);
      setSelectedCard(payload.payment_methods.find(card => card.selected));
    } finally {
      setBusy(false);
    }
  };

  const addCard = async(addedCard) => {
    let success = false;
    setBusy(true);
    try {
      const { payload } = await BillingAPI.addPaymentMethod(providerUuid, addedCard.billing_payment_method_nonce);
      setPaymentMethods(payload.payment_methods);
      setSelectedCard(payload.payment_methods.find(card => card.selected));
      success = true;
    } catch (err) {
      if (err.body) {
        const { message } = err.body;
        setAddErrorMessage(message);
      } else {
        setAddErrorMessage('Card verification failed unexpectedly.');
      }
    } finally {
      setBusy(false);
    }
    return success;
  };

  const onCancel = async () => {
    setCancelBusy(true);
    try {
      await BillingAPI.cancelSubscription(providerUuid);
      getSubscriptionData();
    } catch (err) {
      // console.error(err.body ? err.body : err);
    } finally {
      setCancelBusy(false);
      setShowCancelConfirmation(false);
    }
  };

  useEffect(() => {
    const getPaymentMethods = async() => {
      const { payload } = await BillingAPI.getPaymentMethods(providerUuid);
      setPaymentMethods(payload.payment_methods);
      setSelectedCard(payload.payment_methods.find(card => card.selected));
    };
    const getMatchingFees = async() => {
      const { payload } = await BillingAPI.getNextMatchingFees(providerUuid);
      setMatchingFees(payload);
    };
    getPaymentMethods();
    getMatchingFees();
  }, [providerUuid]);

  useEffect(() => {
    getSubscriptionData();
  }, [providerUuid]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{minWidth: '25rem'}}>
      <Button
        variant="contained"
        label="Add Payment Method"
        cssStyle="large"
        pointer="plus"
        onClick={() => {setShowAddPaymentMethod(true); setAddErrorMessage();}}
      />
      {paymentMethods &&
        <CreditCardList
          cards={paymentMethods}
          selected={selectedCard}
          onSelect={setSelectedCard}
          onDelete={(card) => {
            setCardToDelete(card);
            setShowDeleteConfirmation(true);
          }}
        />
      }

      {paymentMethods && paymentMethods.length > 1 &&
        <Button
          busy={busy}
          variant="contained"
          label="Set Payment Method"
          cssStyle="large"
          pointer="right"
          onClick={onSave}
          disabled={!selectedCard || selectedCard.selected}
        />
      }

      {showPaymentMethodAdvice &&
        <Alert severity="info">{paymentMethodAdvice}</Alert>
      }
      <AccreditationSummary subscription={subscription}/>
      {matchingFees &&
        <MatchingFeesTable matchingFees={matchingFees}/>
      }
      {allowCancel &&
        <Button
          variant="outlined"
          label="Cancel Accreditation"
          cssStyle="large"
          pointer="right"
          onClick={() => setShowCancelConfirmation(true)}
        />
      }
      {allowEnroll &&
        <Button
          variant="outlined"
          label="Get Accredited"
          cssStyle="large"
          pointer="right"
          onClick={getAccredited}
        />
      }

      {showDeleteConfirmation &&
        <Confirmation
          title = "Remove Payment Method"
          message = {`Are you sure you want to remove ${cardToDelete.card_type} ending in ${cardToDelete.last_4} as a payment method?`}
          action = "Remove"
          onConfirm = {() => {deleteCard(cardToDelete); setShowDeleteConfirmation(false);}}
          onCancel = {() => setShowDeleteConfirmation(false)}
          busy = {busy}
        />
      }

      {showCancelConfirmation &&
        <Confirmation
          title = "Cancel Accreditation"
          message = "Are you sure you want to cancel accreditation?"
          action = "Cancel Accreditation"
          onConfirm = {onCancel}
          cancelLabel = "No"
          onCancel = {() => setShowCancelConfirmation(false)}
          busy = {cancelBusy}
        />
      }

      {showAddPaymentMethod &&
        <AddPaymentMethodDialogue
          onCancel = {() => setShowAddPaymentMethod(false)}
          onConfirm = {async (card) => {if (await addCard(card)) setShowAddPaymentMethod(false);}}
          providerUuid = {providerUuid}
          errorMessage = {addErrorMessage}
          busy = {busy}
        />
      }
    </div>
  );
};

BillingSettings.propTypes = {
  providerUuid: PropTypes.string.isRequired,
};

export default BillingSettings;
